/**
 * 자격증 키 통합 시스템
 * CBT 제공 자격증(영문 키)와 전체 자격증(한글 키) 통합 관리
 * jmcd 코드 기반 매핑 시스템 통합
 */

// CBT 제공 자격증들의 영문키-한글명 매핑 (API 데이터와 정확히 일치)
const CBT_MAPPINGS = {
  'electrical-engineer': '전기기사',
  'electrical-industrial-engineer': '전기산업기사', 
  'electrical-construction-engineer': '전기공사기사',
  'electronic-engineer': '전자기사',
  'electronic-industrial-engineer': '전자산업기사',
  'information-processing-engineer': '정보처리기사',
  'information-processing-industrial-engineer': '정보처리산업기사',
  'mechanical-engineer': '일반기계기사',  // API 데이터와 일치하도록 수정
  'mechanical-industrial-engineer': '기계산업기사',
  'architectural-engineer': '건축기사',
  'architectural-industrial-engineer': '건축산업기사',
  'civil-engineer': '토목기사',
  'civil-industrial-engineer': '토목산업기사',
  'chemical-engineer': '화공기사',
  'chemical-industrial-engineer': '화공산업기사',
  // 추가 자격증 매핑 (API 데이터 기준)
  'industrial-safety-engineer': '산업안전기사',
  'fire-safety-mechanical-engineer': '소방설비기사(기계분야)',
  'fire-safety-electrical-engineer': '소방설비기사(전기분야)',
  'air-conditioning-refrigeration-mechanical-engineer': '공조냉동기계기사',
  'agricultural-machinery-engineer': '농업기계기사'
};

// jmcd 코드별 자격증명 매핑 (API 데이터 기반)
// API 데이터에서 자동 추출하여 사용할 예정
let JMCD_MAPPINGS = null;

/**
 * jmcd 매핑 데이터 로드
 * @returns {Promise<Object>} jmcd → 한글 자격증명 매핑
 */
export async function loadJmcdMappings() {
  if (JMCD_MAPPINGS) return JMCD_MAPPINGS;
  
  try {
    const response = await fetch('/qnet-exam-schedules.json');
    const data = await response.json();
    
    JMCD_MAPPINGS = {};
    
    // API 데이터에서 jmcd 코드와 자격증명 추출
    for (const [examName, examInfo] of Object.entries(data.examSchedules)) {
      if (examInfo.jmcd) {
        JMCD_MAPPINGS[examInfo.jmcd] = examName;
      }
    }
    
    return JMCD_MAPPINGS;
  } catch (error) {
    console.error('Failed to load JMCD mappings:', error);
    return {};
  }
}

// 대체 매핑 시스템 (API 데이터에 정확한 이름이 없는 경우)
const ALTERNATIVE_MAPPINGS = {
  '기계기사': ['일반기계기사', '기계설계기사'],
  '소방설비기사': ['소방설비기사(기계분야)', '소방설비기사(전기분야)'],
  '전기설비기사': ['전기기사', '전기설비산업기사'],
  '컴퓨터활용능력': ['컴퓨터활용능력1급', '컴퓨터활용능력2급'],
  '워드프로세서': ['워드프로세서1급', '워드프로세서2급']
};

/**
 * 영문 키를 한글 키로 변환
 * @param {string} englishKey - 영문 자격증 키
 * @returns {string|null} - 한글 자격증명 또는 null
 */
export function englishToKorean(englishKey) {
  return CBT_MAPPINGS[englishKey] || null;
}

/**
 * 한글 키를 영문 키로 변환
 * @param {string} koreanKey - 한글 자격증명
 * @returns {string|null} - 영문 자격증 키 또는 null
 */
export function koreanToEnglish(koreanKey) {
  const entry = Object.entries(CBT_MAPPINGS).find(([_, korean]) => korean === koreanKey);
  return entry ? entry[0] : null;
}

/**
 * 자격증 키 정규화 (CBT 제공 여부와 관계없이 통일된 키 반환)
 * @param {string} examKey - 영문 또는 한글 자격증 키
 * @returns {object} - { key: 실제키, isCBT: CBT제공여부, type: 'english'|'korean' }
 */
export function normalizeExamKey(examKey) {
  if (!examKey) return null;

  // 1. 영문 키인 경우 한글로 변환
  const koreanFromEnglish = englishToKorean(examKey);
  if (koreanFromEnglish) {
    return {
      key: koreanFromEnglish,
      isCBT: true,
      type: 'korean',
      originalKey: examKey,
      englishKey: examKey
    };
  }

  // 2. 한글 키인 경우 CBT 제공 여부 확인
  const englishFromKorean = koreanToEnglish(examKey);
  return {
    key: examKey,
    isCBT: !!englishFromKorean,
    type: 'korean',
    originalKey: examKey,
    englishKey: englishFromKorean
  };
}

/**
 * jmcd 코드로 자격증명 검색
 * @param {number} jmcdCode - jmcd 코드
 * @returns {Promise<string|null>} - 한글 자격증명 또는 null
 */
export async function getExamNameByJmcd(jmcdCode) {
  const mappings = await loadJmcdMappings();
  return mappings[jmcdCode] || null;
}

/**
 * 자격증명으로 jmcd 코드 검색
 * @param {string} examName - 한글 자격증명
 * @returns {Promise<number|null>} - jmcd 코드 또는 null
 */
export async function getJmcdByExamName(examName) {
  const mappings = await loadJmcdMappings();
  for (const [jmcd, name] of Object.entries(mappings)) {
    if (name === examName) {
      return parseInt(jmcd);
    }
  }
  return null;
}

/**
 * 모든 자격증 목록 (jmcd 기반)
 * @returns {Promise<Array>} - 전체 자격증 목록
 */
export async function getAllCertifications() {
  const mappings = await loadJmcdMappings();
  return Object.entries(mappings).map(([jmcd, examName]) => ({
    jmcd: parseInt(jmcd),
    examName,
    isCBT: !!koreanToEnglish(examName)
  }));
}

/**
 * CBT 제공 자격증 목록
 * @returns {Array} - CBT 제공 자격증 목록
 */
export function getCBTCertifications() {
  return Object.entries(CBT_MAPPINGS).map(([englishKey, koreanName]) => ({
    englishKey,
    koreanName,
    isCBT: true
  }));
}

/**
 * 자격증이 CBT를 제공하는지 확인
 * @param {string} examKey - 자격증 키 (영문 또는 한글)
 * @returns {boolean} - CBT 제공 여부
 */
export function isCBTCertification(examKey) {
  const normalized = normalizeExamKey(examKey);
  return normalized ? normalized.isCBT : false;
}

/**
 * 대체 매핑을 통한 자격증명 검색
 * @param {string} examKey - 검색할 자격증 키
 * @returns {Promise<Array<string>>} - 대체 가능한 자격증명 목록
 */
export async function getAlternativeNames(examKey) {
  // 1. 직접 대체 매핑 확인
  if (ALTERNATIVE_MAPPINGS[examKey]) {
    return ALTERNATIVE_MAPPINGS[examKey];
  }

  // 2. jmcd 데이터에서 유사한 이름 검색
  try {
    const jmcdMappings = await loadJmcdMappings();
    const alternatives = [];
    
    for (const [jmcd, examName] of Object.entries(jmcdMappings)) {
      // 부분 매칭으로 유사한 자격증 찾기
      if (examName.includes(examKey) && examName !== examKey) {
        alternatives.push(examName);
      }
      // 역방향 매칭 (examKey가 더 긴 경우)
      else if (examKey.includes(examName) && examName.length >= 3) {
        alternatives.push(examName);
      }
    }

    return alternatives.slice(0, 5); // 최대 5개까지만
  } catch (error) {
    console.warn('대체 매핑 검색 실패:', error);
    return [];
  }
}

// Named exports는 함수 정의와 함께 이미 export 되어 있으므로 제거

export default {
  englishToKorean,
  koreanToEnglish,
  normalizeExamKey,
  getCBTCertifications,
  isCBTCertification,
  loadJmcdMappings,
  getExamNameByJmcd,
  getJmcdByExamName,
  getAllCertifications,
  getAlternativeNames
};